import { useState, useCallback } from "react";
import {
  BarChart3,
  FileText,
  GripVertical,
  Plus,
  Trash2,
  Save,
  Eye,
  X,
  Type,
  Hash,
  PieChart,
  TrendingUp,
  ChevronDown,
  LayoutTemplate,
} from "lucide-react";
import { exportCSV, exportJSON, exportPDF } from "../utils/exportData";

import { useTranslation } from 'react-i18next';
// ─── Available Widget Types ───
const WIDGET_TYPES = [
  { type: "kpi", label: "KPI Card", icon: Hash, description: "Key performance indicator" },
  { type: "chart-line", label: "Line Chart", icon: TrendingUp, description: "Time-series line chart" },
  { type: "chart-bar", label: "Bar Chart", icon: BarChart3, description: "Vertical bar chart" },
  { type: "chart-pie", label: "Pie Chart", icon: PieChart, description: "Donut/pie distribution" },
  { type: "table", label: "Data Table", icon: LayoutTemplate, description: "Tabular data display" },
  { type: "text", label: "Text Block", icon: Type, description: "Rich text content" },
];

// ─── Pre-defined Templates ───
const TEMPLATES = [
  {
    id: "exec-summary",
    name: "Executive Summary",
    description: "High-level KPIs and trends for leadership",
    widgets: [
      { id: "w1", type: "kpi", title: "Messages Today", value: "1,247", trend: "+12.5%" },
      { id: "w2", type: "kpi", title: "Avg Response", value: "1.2s", trend: "-0.3s" },
      { id: "w3", type: "kpi", title: "NPS Score", value: "72", trend: "+5 pts" },
      { id: "w4", type: "kpi", title: "Cost Saved", value: "$7,020", trend: "+18.5%" },
      { id: "w5", type: "chart-line", title: "Message Volume (30d)" },
      { id: "w6", type: "chart-pie", title: "Channel Distribution" },
      { id: "w7", type: "text", title: "Key Insights", content: "Message volume increased 12.5% vs last period. Response time improved by 0.3s. WhatsApp remains the dominant channel at 42%." },
    ],
  },
  {
    id: "team-perf",
    name: "Team Performance",
    description: "Team member metrics and leaderboard",
    widgets: [
      { id: "w1", type: "kpi", title: "Team Tasks", value: "591", trend: "+71" },
      { id: "w2", type: "kpi", title: "Avg Satisfaction", value: "91.8%", trend: "+1.2%" },
      { id: "w3", type: "kpi", title: "Avg Response", value: "1.4s", trend: "-0.1s" },
      { id: "w4", type: "kpi", title: "Resolution Rate", value: "80.3%", trend: "+2.1%" },
      { id: "w5", type: "chart-bar", title: "Tasks per Member" },
      { id: "w6", type: "table", title: "Leaderboard" },
      { id: "w7", type: "chart-line", title: "Weekly Trend" },
    ],
  },
  {
    id: "channel-analytics",
    name: "Channel Analytics",
    description: "Deep-dive into channel performance",
    widgets: [
      { id: "w1", type: "kpi", title: "WhatsApp", value: "42%", trend: "+3.2%" },
      { id: "w2", type: "kpi", title: "Telegram", value: "28%", trend: "+1.8%" },
      { id: "w3", type: "kpi", title: "Email", value: "18%", trend: "-0.5%" },
      { id: "w4", type: "chart-pie", title: "Channel Split" },
      { id: "w5", type: "chart-bar", title: "Messages by Channel" },
      { id: "w6", type: "table", title: "Channel Details" },
      { id: "w7", type: "text", title: "Channel Notes", content: "WhatsApp leads with 42% share. Telegram growing fastest at +1.8% WoW. Email declining slightly." },
    ],
  },
];

// ─── Widget Renderer ───
function WidgetRenderer({ widget, viewMode, onExport }) {
  const typeConfig = WIDGET_TYPES.find((w) => w.type === widget.type) || WIDGET_TYPES[0];
  const Icon = typeConfig.icon;

  const handleExport = (format) => {
    if (onExport) {
      const data = generateWidgetData(widget);
      const name = widget.title || widget.type;
      if (format === "csv") exportCSV(data, name);
      else if (format === "json") exportJSON(data, name);
      else if (format === "pdf") exportPDF(data, name, name);
    }
  };

  return (
    <div className={`border border-zinc-800 bg-zinc-950/30 ${viewMode === "builder" ? "relative group" : ""}`}>
      {viewMode === "builder" && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 flex gap-1">
          <button
            onClick={() => handleExport("csv")}
            className="px-2 py-1 bg-zinc-800 hover:bg-amber-500 hover:text-black text-zinc-400 text-[9px] font-mono uppercase transition-colors"
            title={t('report_builder.export_csv')}
          >
            CSV
          </button>
          <button
            onClick={() => handleExport("json")}
            className="px-2 py-1 bg-zinc-800 hover:bg-amber-500 hover:text-black text-zinc-400 text-[9px] font-mono uppercase transition-colors"
            title={t('report_builder.export_json')}
          >
            JSON
          </button>
          <button
            onClick={() => handleExport("pdf")}
            className="px-2 py-1 bg-zinc-800 hover:bg-amber-500 hover:text-black text-zinc-400 text-[9px] font-mono uppercase transition-colors"
            title={t('report_builder.export_pdf')}
          >
            PDF
          </button>
        </div>
      )}
      <div className="p-4">
        {widget.type === "kpi" && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-4 h-4 text-amber-500" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {widget.title || "KPI Card"}
              </span>
            </div>
            <div className="font-display text-2xl text-white">{widget.value || "—"}</div>
            {widget.trend && (
              <div className="font-mono text-xs text-emerald-400 mt-1">{widget.trend}</div>
            )}
          </div>
        )}
        {widget.type === "chart-line" && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-4 h-4 text-amber-500" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {widget.title || "Line Chart"}
              </span>
            </div>
            <div className="h-32 border border-zinc-800/50 bg-zinc-900/30 flex items-center justify-center">
              <span className="font-mono text-[10px] text-zinc-600 uppercase">
                [Line Chart Placeholder]
              </span>
            </div>
          </div>
        )}
        {widget.type === "chart-bar" && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-4 h-4 text-amber-500" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {widget.title || "Bar Chart"}
              </span>
            </div>
            <div className="h-32 border border-zinc-800/50 bg-zinc-900/30 flex items-center justify-center">
              <span className="font-mono text-[10px] text-zinc-600 uppercase">
                [Bar Chart Placeholder]
              </span>
            </div>
          </div>
        )}
        {widget.type === "chart-pie" && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-4 h-4 text-amber-500" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {widget.title || "Pie Chart"}
              </span>
            </div>
            <div className="h-32 border border-zinc-800/50 bg-zinc-900/30 flex items-center justify-center">
              <span className="font-mono text-[10px] text-zinc-600 uppercase">
                [Pie Chart Placeholder]
              </span>
            </div>
          </div>
        )}
        {widget.type === "table" && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-4 h-4 text-amber-500" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {widget.title || "Data Table"}
              </span>
            </div>
            <div className="border border-zinc-800/50 bg-zinc-900/30 p-3">
              <span className="font-mono text-[10px] text-zinc-600 uppercase">
                [Table Placeholder]
              </span>
            </div>
          </div>
        )}
        {widget.type === "text" && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-4 h-4 text-amber-500" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {widget.title || "Text Block"}
              </span>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              {widget.content || "Add your text content here..."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Generate mock data for widget export ───
function generateWidgetData(widget) {
  if (widget.type === "kpi") {
    return [{ metric: widget.title, value: widget.value, trend: widget.trend }];
  }
  if (widget.type.startsWith("chart")) {
    return [
      { label: "WhatsApp", value: 42 },
      { label: "Telegram", value: 28 },
      { label: "Email", value: 18 },
      { label: "Discord", value: 8 },
      { label: "Slack", value: 4 },
    ];
  }
  if (widget.type === "table") {
    return [
      { member: "Ana Silva", tasks: 147, score: 96.2 },
      { member: "Carlos Mendes", tasks: 132, score: 94.8 },
      { member: "Beatriz Costa", tasks: 118, score: 92.1 },
    ];
  }
  return [{ content: widget.content || widget.title }];
}

// ─── Main Component ───
export default function ReportBuilder({ onClose }) {
  const { t } = useTranslation();
  const [reportName, setReportName] = useState("My Custom Report");
  const [widgets, setWidgets] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [viewMode, setViewMode] = useState("builder"); // 'builder' | 'preview'
  const [savedReports, setSavedReports] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("max-reports") || "[]");
    } catch {
      return [];
    }
  });
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Add widget to report
  const addWidget = useCallback((type) => {
    const id = `widget-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const typeConfig = WIDGET_TYPES.find((w) => w.type === type);
    setWidgets((prev) => [
      ...prev,
      {
        id,
        type,
        title: typeConfig ? typeConfig.label : type,
        value: type === "kpi" ? "1,247" : undefined,
        trend: type === "kpi" ? "+12.5%" : undefined,
        content: type === "text" ? "Add your insights here..." : undefined,
      },
    ]);
    setShowWidgetPicker(false);
  }, []);

  // Remove widget
  const removeWidget = useCallback((id) => {
    setWidgets((prev) => prev.filter((w) => w.id !== id));
  }, []);

  // Load template
  const loadTemplate = useCallback((template) => {
    setReportName(template.name);
    setWidgets(template.widgets.map((w) => ({ ...w, id: `widget-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` })));
    setShowTemplates(false);
  }, []);

  // Save report to localStorage
  const saveReport = useCallback(() => {
    const report = {
      id: `report-${Date.now()}`,
      name: reportName,
      widgets,
      createdAt: new Date().toISOString(),
    };
    const updated = [...savedReports, report];
    setSavedReports(updated);
    localStorage.setItem("max-reports", JSON.stringify(updated));
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 2000);
  }, [reportName, widgets, savedReports]);

  // Load saved report
  const loadReport = useCallback((report) => {
    setReportName(report.name);
    setWidgets(report.widgets);
  }, []);

  // Delete saved report
  const deleteReport = useCallback((id) => {
    const updated = savedReports.filter((r) => r.id !== id);
    setSavedReports(updated);
    localStorage.setItem("max-reports", JSON.stringify(updated));
  }, [savedReports]);

  // Drag and drop reorder
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const newWidgets = [...widgets];
    const [dragged] = newWidgets.splice(draggedIndex, 1);
    newWidgets.splice(index, 0, dragged);
    setWidgets(newWidgets);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <FileText className="w-5 h-5 text-amber-500" />
          <input
            type="text"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            className="bg-transparent border-b border-zinc-700 focus:border-amber-500 text-white font-display text-lg uppercase outline-none px-1 py-0.5 transition-colors"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View Toggle */}
          <button
            onClick={() => setViewMode(viewMode === "builder" ? "preview" : "builder")}
            className="flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
            {viewMode === "builder" ? "Preview" : "Edit"}
          </button>
          {/* Templates */}
          <div className="relative">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 transition-colors"
            >
              <LayoutTemplate className="w-3.5 h-3.5 text-amber-500" />
              Templates
              <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
            </button>
            {showTemplates && (
              <div className="absolute top-full right-0 mt-1 border border-zinc-800 bg-zinc-950 shadow-xl z-20 min-w-[280px]">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => loadTemplate(t)}
                    className="w-full text-left p-3 hover:bg-zinc-900 transition-colors border-b border-zinc-800 last:border-0"
                  >
                    <div className="font-display text-sm text-white uppercase">{t.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-1">{t.description}</div>
                    <div className="font-mono text-[9px] text-amber-500/70 mt-1">
                      {t.widgets.length} widgets
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Save */}
          <button
            onClick={saveReport}
            className="flex items-center gap-2 border border-amber-500/50 hover:border-amber-500 bg-amber-500/5 hover:bg-amber-500/10 text-amber-500 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
          >
            <Save className="w-3.5 h-3.5" />
            Save
          </button>
        </div>
      </div>

      {/* Save Toast */}
      {showSaveToast && (
        <div className="fixed top-4 right-4 z-50 bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 px-4 py-3 font-mono text-xs uppercase tracking-[0.1em] shadow-xl">
          Report saved successfully
        </div>
      )}

      {/* Saved Reports */}
      {savedReports.length > 0 && (
        <div className="border border-zinc-800 bg-zinc-950/20 p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
            Saved Reports ({savedReports.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {savedReports.map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-2 border border-zinc-800 bg-zinc-950/50 px-3 py-1.5 group"
              >
                <button
                  onClick={() => loadReport(r)}
                  className="font-mono text-[10px] text-zinc-300 hover:text-amber-500 uppercase transition-colors"
                >
                  {r.name}
                </button>
                <button
                  onClick={() => deleteReport(r.id)}
                  className="text-zinc-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Widget Picker */}
      {viewMode === "builder" && (
        <div className="border border-dashed border-zinc-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('report_builder.add_widgets')}</span>
            <button
              onClick={() => setShowWidgetPicker(!showWidgetPicker)}
              className="flex items-center gap-1 text-amber-500 hover:text-amber-400 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Widget
            </button>
          </div>
          {showWidgetPicker && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {WIDGET_TYPES.map((wt) => {
                const WIcon = wt.icon;
                return (
                  <button
                    key={wt.type}
                    onClick={() => addWidget(wt.type)}
                    className="flex items-center gap-3 border border-zinc-800 hover:border-amber-500/50 bg-zinc-950/30 hover:bg-amber-500/5 p-3 transition-colors text-left"
                  >
                    <WIcon className="w-4 h-4 text-amber-500 shrink-0" />
                    <div>
                      <div className="font-display text-xs text-white uppercase">{wt.label}</div>
                      <div className="font-mono text-[9px] text-zinc-500">{wt.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Report Canvas */}
      {widgets.length === 0 ? (
        <div className="border border-dashed border-zinc-700 p-12 text-center">
          <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="font-mono text-xs text-zinc-500 uppercase tracking-[0.1em]">{t('report_builder.no_widgets_yet_add_widgets_or_load_a_template_to')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {widgets.map((widget, index) => (
            <div
              key={widget.id}
              draggable={viewMode === "builder"}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`${viewMode === "builder" ? "cursor-move" : ""} ${draggedIndex === index ? "opacity-50" : ""}`}
            >
              {viewMode === "builder" && (
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1 text-zinc-600">
                    <GripVertical className="w-3 h-3" />
                    <span className="font-mono text-[9px] uppercase">{widget.type}</span>
                  </div>
                  <button
                    onClick={() => removeWidget(widget.id)}
                    className="text-zinc-600 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              )}
              <WidgetRenderer widget={widget} viewMode={viewMode} />
            </div>
          ))}
        </div>
      )}

      {/* Export Report */}
      {widgets.length > 0 && (
        <div className="border border-zinc-800 bg-zinc-950/20 p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
            Export Report
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const data = widgets.map((w) => ({
                  type: w.type,
                  title: w.title,
                  value: w.value || "",
                  content: w.content || "",
                }));
                exportCSV(data, reportName.replace(/\s+/g, "_"));
              }}
              className="flex items-center gap-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-400 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              Export CSV
            </button>
            <button
              onClick={() => {
                const data = widgets.map((w) => ({
                  type: w.type,
                  title: w.title,
                  value: w.value || "",
                  content: w.content || "",
                }));
                exportJSON(data, reportName.replace(/\s+/g, "_"));
              }}
              className="flex items-center gap-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-400 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              Export JSON
            </button>
            <button
              onClick={() => {
                const data = widgets.map((w) => ({
                  type: w.type,
                  title: w.title,
                  value: w.value || "",
                  content: w.content || "",
                }));
                exportPDF(data, reportName.replace(/\s+/g, "_"), reportName);
              }}
              className="flex items-center gap-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-400 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              Export PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
